import { useCallback, useEffect, useRef, useState } from 'react';

// The Web Speech API has no official TS lib types; declare just the surface
// the legacy app used (recognition.lang/onresult/onerror/onend, single
// non-interim result with alternatives).
interface SpeechRecognitionAlternative {
  transcript: string;
}
interface SpeechRecognitionResult {
  [index: number]: SpeechRecognitionAlternative;
  length: number;
}
interface SpeechRecognitionEvent {
  results: { [index: number]: SpeechRecognitionResult };
}
interface SpeechRecognitionErrorEvent {
  error: string;
}
interface SpeechRecognitionLike {
  lang: string;
  interimResults: boolean;
  maxAlternatives: number;
  continuous: boolean;
  onstart: (() => void) | null;
  onend: (() => void) | null;
  onerror: ((e: SpeechRecognitionErrorEvent) => void) | null;
  onresult: ((e: SpeechRecognitionEvent) => void) | null;
  start: () => void;
  stop: () => void;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): SpeechRecognitionCtor | null {
  const w = window as unknown as { SpeechRecognition?: SpeechRecognitionCtor; webkitSpeechRecognition?: SpeechRecognitionCtor };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/** Same friendly copy the legacy app showed for each Web Speech error code. */
const ERROR_MESSAGES: Record<string, string> = {
  'not-allowed': 'Mic access denied. Allow it in your browser settings.',
  'no-speech': 'No speech detected. Try again.',
  network: 'Network error.',
};

/**
 * Speech-to-text via the browser's Web Speech API — the same API the legacy
 * app used, ported to a hook instead of vanilla DOM manipulation. Supported
 * in Chrome/Edge/Safari; `supported` is false elsewhere (notably Firefox),
 * so callers should hide the mic button rather than render a dead one.
 */
export function useSpeechToText(onResult: (transcript: string, alternatives: string[]) => void) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  const supported = getCtor() !== null;

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;

    const recognition = new Ctor();
    recognition.lang = 'en-IN';
    recognition.interimResults = false;
    recognition.maxAlternatives = 3;
    recognition.continuous = false;

    recognition.onstart = () => {
      setListening(true);
      setError(null);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = (e) => {
      setListening(false);
      setError(ERROR_MESSAGES[e.error] ?? null);
    };
    recognition.onresult = (e) => {
      const result = e.results[0];
      if (!result) return;
      const alternatives: string[] = [];
      for (let i = 0; i < result.length; i++) {
        const alt = result[i];
        if (alt) alternatives.push(alt.transcript);
      }
      onResultRef.current(alternatives[0] ?? '', alternatives);
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      /* already listening — ignore */
    }
  }, []);

  const stop = useCallback(() => recognitionRef.current?.stop(), []);

  const toggle = useCallback(() => (listening ? stop() : start()), [listening, start, stop]);

  return { supported, listening, error, start, stop, toggle };
}
